import { useRef, useState } from 'react';
import { Lock, LockOpen, Paperclip, Users, X } from 'lucide-react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { TipoNota, Visibilidade, type AnexoDto, type NotaDto } from '@nexital/shared';
import { useDependencias } from '../../application/dependencias';
import { ligadaACartao, podeAnexar, seloVisibilidade, visibilidadeAoCompartilhar, visibilidadePeloCadeado } from '../../domain/notas';
import { Avatar } from '../design-system/Avatar';
import { useConfirmar } from '../design-system/Confirmacao';
import { useNotas } from './contexto-notas';
import { DialogoCompartilhar } from './DialogoCompartilhar';
import type { NoNotaTipo } from './montar-nos-notas';
import './notas.css';

const COR_PADRAO = '#FFE58A';
const CORES = [COR_PADRAO, '#FFC6A8', '#BDE7C1', '#BFDDFB', '#E2CCF7'];

/** Texto editável no lugar por duplo clique; grava ao sair do campo. */
function TextoNota({ nota }: { nota: NotaDto }) {
  const alterar = useNotas((s) => s.alterarNota);
  const [editando, setEditando] = useState(false);
  if (!editando) {
    return (
      <div className="nota__texto" onDoubleClick={() => nota.podeEditar && setEditando(true)}>
        {nota.texto || <span className="nota__dica">Duplo clique para escrever</span>}
      </div>
    );
  }
  return (
    <textarea
      className="nota__editor nodrag nowheel"
      autoFocus
      defaultValue={nota.texto}
      aria-label="Texto da nota"
      onBlur={(e) => {
        setEditando(false);
        if (e.target.value !== nota.texto) void alterar(nota.uuid, { texto: e.target.value });
      }}
    />
  );
}

/** Anexos da nota: abrem por URL assinada (a janela abre no clique para o navegador não bloquear); quem edita exclui (RQ-NOT-009). */
function AnexosNota({ nota }: { nota: NotaDto }) {
  const { urlAnexo, excluirAnexo } = useNotas((s) => s);
  const confirmar = useConfirmar();
  if (!nota.anexos.length) return null;
  const abrir = async (arquivoUuid: string) => {
    const janela = window.open('', '_blank');
    const url = await urlAnexo(nota.uuid, arquivoUuid);
    if (url && janela) janela.location.href = url.url;
    else janela?.close();
  };
  const excluir = async (anexo: AnexoDto) => {
    const pedido = { titulo: 'Excluir o anexo?', mensagem: `"${anexo.nome}" sai do post-it para todos.`, rotulo: 'Excluir' };
    if (await confirmar(pedido)) void excluirAnexo(nota.uuid, anexo.uuid);
  };
  return (
    <ul className="nota__anexos nodrag">
      {nota.anexos.map((a) => (
        <li key={a.uuid}>
          <button type="button" className="nota__anexo" onClick={() => void abrir(a.uuid)} title={a.nome}>
            <Paperclip size={12} aria-hidden /> {a.nome}
          </button>
          {nota.podeEditar && (
            <button type="button" className="nota__anexo-excluir" onClick={() => void excluir(a)} title={`Excluir ${a.nome}`} aria-label={`Excluir o anexo ${a.nome}`}>
              <X size={12} aria-hidden />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Menu da nota: cor, compartilhar (só o autor), anexar e excluir. A visibilidade muda pelo cadeado (RQ-NOT-007). */
function MenuNota({ nota, souAutor, aoFechar }: { nota: NotaDto; souAutor: boolean; aoFechar: () => void }) {
  const { alterarNota, excluirNota, anexar, pessoasQuadro, setas, dentroDaLicitacao } = useNotas((s) => s);
  const confirmar = useConfirmar();
  const excluir = async () => {
    if (await confirmar({ titulo: 'Excluir a nota?', mensagem: 'A nota sai do quadro para todos. Não dá para desfazer.', rotulo: 'Excluir' })) void excluirNota(nota.uuid);
  };
  const seletor = useRef<HTMLInputElement>(null);
  const [compartilhando, setCompartilhando] = useState(false);
  const corAtual = nota.cor ?? COR_PADRAO;
  if (compartilhando) {
    return (
      <DialogoCompartilhar
        pessoas={pessoasQuadro.filter((p) => p.uuid !== nota.autor.uuid)}
        selecionadas={nota.compartilhadaCom.map((p) => p.uuid)}
        exigeCartao={ligadaACartao(nota.uuid, setas, dentroDaLicitacao)}
        aoCancelar={aoFechar}
        aoConfirmar={(uuids) => {
          void alterarNota(nota.uuid, { visibilidade: visibilidadeAoCompartilhar(nota.visibilidade, uuids.length), compartilharCom: uuids });
          aoFechar();
        }}
      />
    );
  }
  return (
    <div className="nota__menu nodrag" role="menu">
      <div className="nota__cores">
        {CORES.map((cor) => (
          <button
            key={cor}
            type="button"
            className="nota__cor"
            style={{ background: cor }}
            aria-label={`Cor ${cor}`}
            aria-pressed={cor.toLowerCase() === corAtual.toLowerCase()}
            onClick={() => cor !== nota.cor && void alterarNota(nota.uuid, { cor })}
          />
        ))}
      </div>
      {souAutor && <button type="button" role="menuitem" onClick={() => setCompartilhando(true)}>Compartilhar com…</button>}
      {podeAnexar(nota) && <button type="button" role="menuitem" onClick={() => seletor.current?.click()}>Anexar arquivo…</button>}
      <input
        ref={seletor}
        type="file"
        hidden
        onChange={(e) => {
          const arquivo = e.target.files?.[0];
          if (arquivo) void anexar(nota.uuid, arquivo);
          aoFechar();
        }}
      />
      <button type="button" role="menuitem" className="nota__excluir" onClick={() => void excluir()}>Excluir</button>
    </div>
  );
}

/** Quem vê a nota, em palavras, para a dica do cadeado. */
function quemVe(nota: NotaDto): string {
  if (nota.visibilidade === Visibilidade.Publica) return 'Post-it público: todos do quadro veem';
  const superiores = nota.vistaPorSuperiores ? ` (${nota.vistaPorSuperiores} podem ver)` : '';
  if (nota.visibilidade === Visibilidade.Compartilhada) return `Compartilhado com ${nota.compartilhadaCom.map((p) => p.nome.split(' ')[0]).join(', ')}${superiores}`;
  return `Post-it privado: só você vê${superiores}`;
}

/** O que clicar no cadeado faz, ou por que a pessoa não pode clicar. */
function acaoDoCadeado(publica: boolean, pode: boolean, souAutor: boolean): string {
  if (pode) return publica ? ' Clique para tornar privado.' : ' Clique para tornar público.';
  return souAutor && !publica ? ' Você não tem permissão para tornar post-its públicos.' : '';
}

/** Pergunta antes de trocar a visibilidade pelo cadeado. */
function pedidoDoCadeado(nova: Visibilidade) {
  if (nova === Visibilidade.Publica) return { titulo: 'Tornar o post-it público?', mensagem: 'Todas as pessoas do quadro passarão a ver este post-it.', rotulo: 'Tornar público' };
  const mensagem = nova === Visibilidade.Compartilhada ? 'Só você e as pessoas com quem ele está compartilhado verão o post-it.' : 'Só você verá o post-it.';
  return { titulo: 'Tornar o post-it privado?', mensagem, rotulo: 'Tornar privado' };
}

/**
 * Cadeado (RQ-NOT-007): fechado na nota privada ou compartilhada, aberto na pública. O autor clica e, depois de
 * confirmar, a nota passa a pública (exige "Criar notas públicas") ou volta a não pública.
 */
function Cadeado({ nota, souAutor }: { nota: NotaDto; souAutor: boolean }) {
  const { alterarNota, podeTornarPublica } = useNotas((s) => s);
  const confirmar = useConfirmar();
  if (nota.acompanhada) return <span className="pilula nota__selo">{seloVisibilidade(nota)}</span>;
  const publica = nota.visibilidade === Visibilidade.Publica;
  const Icone = publica ? LockOpen : Lock;
  const pode = souAutor && nota.podeEditar && (publica || podeTornarPublica);
  const dica = `${quemVe(nota)}.${acaoDoCadeado(publica, pode, souAutor)}`;
  const alternar = async () => {
    const nova = visibilidadePeloCadeado(nota);
    if (await confirmar({ ...pedidoDoCadeado(nova), perigo: false })) void alterarNota(nota.uuid, { visibilidade: nova });
  };
  return (
    <span className="nota__visibilidade">
      <button type="button" className="nota__cadeado nodrag" title={dica} aria-label={dica} disabled={!pode} onClick={() => void alternar()}>
        <Icone size={14} aria-hidden />
      </button>
      {nota.visibilidade === Visibilidade.Compartilhada && (
        <span className="nota__compartilhada" title={quemVe(nota)}>
          <Users size={13} aria-hidden /> {nota.compartilhadaCom.length}
        </span>
      )}
    </span>
  );
}

/** Post-it no quadro (RQ-NOT-001, RQ-NOT-003); notas de texto antigas continuam aparecendo. */
export function NoNota({ data, selected }: NodeProps<NoNotaTipo>) {
  const { nota } = data;
  const { sessao } = useDependencias();
  const [menuAberto, setMenuAberto] = useState(false);
  const souAutor = nota.autor.uuid === sessao.usuarioUuid;
  const texto = nota.tipo === TipoNota.Texto;
  const acompanhada = nota.acompanhada ? ' nota--acompanhada' : '';
  const classe = `nota ${texto ? 'nota--texto' : 'nota--postit'}${selected ? ' nota--selecionada' : ''}${acompanhada}`;
  return (
    <div className={classe} style={texto ? undefined : { background: nota.cor ?? COR_PADRAO }}>
      <Handle type="target" position={Position.Left} className="nota__ponta" />
      <Handle type="source" position={Position.Right} className="nota__ponta" />
      <TextoNota nota={nota} />
      <AnexosNota nota={nota} />
      <footer className="nota__rodape">
        <Avatar nome={nota.autor.nome} chave={nota.autor.uuid} tamanho={20} />
        {nota.acompanhada && <span className="nota__autor">{nota.autor.nome.split(' ')[0]}</span>}
        <Cadeado nota={nota} souAutor={souAutor} />
        {nota.podeEditar && (
          <button type="button" className="botao-icone nota__mais nodrag" aria-label="Opções da nota" onClick={() => setMenuAberto((v) => !v)}>
            ⋯
          </button>
        )}
      </footer>
      {menuAberto && <MenuNota nota={nota} souAutor={souAutor} aoFechar={() => setMenuAberto(false)} />}
    </div>
  );
}
